import React from 'react';
import { Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';


const round2 = (val) => typeof val === 'number' ? val.toFixed(2) : val;

const TabContent = ({ tabIndex, selfAssessment, documents, overall }) => {
  const theme = useTheme();

  if (tabIndex === 0) {
    const scores = selfAssessment?.scores || {};
    const overallPercent = selfAssessment?.overall_percent || 0;

    return (
      <Box>
        <Typography variant="h6" sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 2 }}>
          Self-Assessment Scores
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>Criteria</TableCell>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>Score</TableCell>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>Max</TableCell>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(scores).map(([label, data]) => (
                <TableRow key={label}>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{label}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{data.score}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{data.max_score}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{round2(data.percent)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mt: 2 }}>
          <b>Overall Self-Assessment Score:</b> {round2(overallPercent)}%
        </Typography>
      </Box>
    );
  }

  if (tabIndex === 1) {
    return (
      <Box>
        <Typography variant="h6" sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 2 }}>
          Document Scores & Comments
        </Typography>
        <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontWeight: "bold",
            mb: 1,
          }}
        >
          Scoring:
        </Typography>
        <List dense>
          <ListItem>Fully present, clear, complete, consistent: 10 points</ListItem>
          <ListItem>Partially present/incomplete: 5 points</ListItem>
          <ListItem>Missing or inconsistent: 0 points</ListItem>
        </List>
        </Paper>
        <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper, mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, fontWeight: "bold" }}>Document</TableCell>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, fontWeight: "bold" }}>Score</TableCell>
                <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, fontWeight: "bold" }}>Comments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(documents || {}).map(([filename, result]) => (
                <TableRow key={filename}>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{filename}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>{result.score}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily, whiteSpace: 'pre-line' }}>
                    {Array.isArray(result.comments)
                      ? result.comments.map((c, i) => <div key={i}>{c}</div>)
                      : result.comments}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (tabIndex === 2) {
    const strengths = overall?.strengths || [];
    const weaknesses = overall?.weaknesses || [];
    const maxRows = Math.max(strengths.length, weaknesses.length, 1);

    return (
      <Box>
        <Typography variant="h6" sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 2 }}>
          Overall Results
        </Typography>
        <Typography sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 1 }}>
          <b>Self-Assessment Average:</b> {round2(overall?.self_assessment_percent) || 0}%
        </Typography>
        <Typography sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 1 }}>
          <b>Document Score Average:</b> {round2(overall?.document_score_avg * 10) || 0}%
        </Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: theme.palette.background.paper, mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 'bold',
                    width: '50%',
                    borderRight: `1px solid ${theme.palette.text.primary}`,
                  }}
                >
                  Strengths (≥75%)
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 'bold',
                    width: '50%',
                  }}
                >
                  Weaknesses/Areas for Improvement (&lt;50%)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: maxRows }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell
                    sx={{
                      color: theme.palette.text.primary,
                      fontFamily: theme.typography.fontFamily,
                      borderRight: `1px solid ${theme.palette.text.primary}`,
                    }}
                  >
                    {strengths[idx] || (idx === 0 ? 'None' : '')}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}>
                    {weaknesses[idx] || (idx === 0 ? 'None' : '')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return null;
};

export default TabContent;